import { useState, useEffect, useCallback, useRef } from 'react';
import { getItems, createItem, updateItem, deleteItem } from '../fitness/fitnessApi';

const CORE_LIST = 'CoreMemory';
const WORKING_LIST = 'WorkingMemory';
const MAX_WORKING = 1000;
const MAX_CONTEXT_TOKENS = 8000; // approximate char budget for system prompt injection
const CHARS_PER_TOKEN = 4;

export function useMemory(getToken, userEmail) {
  const [coreMemories, setCoreMemories] = useState([]);
  const [workingMemories, setWorkingMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const [coreItems, workingItems] = await Promise.all([
        getItems(token, CORE_LIST),
        getItems(token, WORKING_LIST),
      ]);

      // Core: shared — load all
      const core = coreItems.map(mapCore).sort((a, b) => b.timesUsed - a.timesUsed);

      // Working: personal — filter by user
      const working = workingItems
        .filter(it => (it.CreatedBy || '').toLowerCase() === userEmail.toLowerCase())
        .map(mapWorking)
        .sort((a, b) => b.relevanceScore - a.relevanceScore);

      setCoreMemories(core);
      setWorkingMemories(working);
      loadedRef.current = true;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build context string for JBB system prompt
  function buildMemoryContext() {
    const coreLines = coreMemories.map(m => `- [CORE] ${m.fact} (id:${m.id})`);
    const coreText = coreLines.join('\n');
    const coreChars = coreText.length;

    // Budget remaining for working memory
    const budgetChars = MAX_CONTEXT_TOKENS * CHARS_PER_TOKEN;
    const remainingBudget = budgetChars - coreChars - 200; // 200 char buffer for headers

    let workingLines = workingMemories.map(m => `- [WORKING] ${m.fact} (id:${m.id})`);
    let workingText = workingLines.join('\n');

    // Trim working memory from lowest relevance if over budget
    if (workingText.length > remainingBudget && remainingBudget > 0) {
      // Already sorted by relevanceScore desc — trim from end
      while (workingText.length > remainingBudget && workingLines.length > 0) {
        workingLines.pop();
        workingText = workingLines.join('\n');
      }
    } else if (remainingBudget <= 0) {
      workingLines = [];
      workingText = '';
    }

    if (coreLines.length === 0 && workingLines.length === 0) return '';

    let context = '\nMEMORY BANK:';
    if (coreLines.length > 0) {
      context += `\nCore Memory (${coreLines.length} facts — permanent, shared):\n${coreText}`;
    }
    if (workingLines.length > 0) {
      context += `\nWorking Memory (${workingLines.length} facts — personal, auto-saved):\n${workingText}`;
    }
    return context;
  }

  // Save a new working memory
  const saveWorking = useCallback(async (fact, expirationDate = null) => {
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      await createItem(token, WORKING_LIST, {
        Title: fact.substring(0, 50),
        Fact: fact,
        CreatedDate: now,
        LastUsed: now,
        TimesUsed: 0,
        ExpirationDate: expirationDate || '',
        RelevanceScore: 50, // start at midpoint
        CreatedBy: userEmail,
      });
      await fetchAll();
    } catch (err) {
      console.warn('Failed to save working memory:', err.message);
    }
  }, [getToken, userEmail, fetchAll]);

  // Save a new core memory
  const saveCore = useCallback(async (fact, promotedFrom = 'manual') => {
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      await createItem(token, CORE_LIST, {
        Title: fact.substring(0, 50),
        Fact: fact,
        CreatedDate: now,
        LastUsed: now,
        TimesUsed: 0,
        PromotedFrom: promotedFrom,
        FlaggedForReview: false,
        CreatedBy: userEmail,
      });
      await fetchAll();
    } catch (err) {
      console.warn('Failed to save core memory:', err.message);
    }
  }, [getToken, userEmail, fetchAll]);

  // Update usage for referenced memories
  const markUsed = useCallback(async (memoryIds) => {
    if (!memoryIds || memoryIds.length === 0) return;
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      for (const id of memoryIds) {
        // Check if it's a core or working memory
        const core = coreMemories.find(m => m.id === id);
        const working = workingMemories.find(m => m.id === id);
        if (core) {
          await updateItem(token, CORE_LIST, id, {
            TimesUsed: (core.timesUsed || 0) + 1,
            LastUsed: now,
            FlaggedForReview: false, // unflag if it was flagged
          });
        } else if (working) {
          const newTimes = (working.timesUsed || 0) + 1;
          const daysSinceCreation = Math.max(1, (Date.now() - new Date(working.createdDate).getTime()) / 86400000);
          const newScore = Math.min(100, Math.round((newTimes * 10) + (100 / daysSinceCreation)));
          await updateItem(token, WORKING_LIST, id, {
            TimesUsed: newTimes,
            LastUsed: now,
            RelevanceScore: newScore,
          });
        }
      }
    } catch (err) {
      console.warn('Failed to update memory usage:', err.message);
    }
  }, [getToken, coreMemories, workingMemories]);

  // Update an existing memory's fact text
  const updateMemory = useCallback(async (id, isCore, newFact) => {
    try {
      const token = await getToken();
      const list = isCore ? CORE_LIST : WORKING_LIST;
      await updateItem(token, list, id, {
        Fact: newFact,
        Title: newFact.substring(0, 50),
      });
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, fetchAll]);

  // Delete a memory
  const deleteMemory = useCallback(async (id, isCore) => {
    try {
      const token = await getToken();
      const list = isCore ? CORE_LIST : WORKING_LIST;
      await deleteItem(token, list, id);
      if (isCore) {
        setCoreMemories(prev => prev.filter(m => m.id !== id));
      } else {
        setWorkingMemories(prev => prev.filter(m => m.id !== id));
      }
    } catch (err) {
      setError(err.message);
    }
  }, [getToken]);

  // Move working memory to core
  const promoteToCore = useCallback(async (workingId) => {
    const working = workingMemories.find(m => m.id === workingId);
    if (!working) return;
    try {
      await saveCore(working.fact, workingId);
      const token = await getToken();
      await deleteItem(token, WORKING_LIST, workingId);
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, workingMemories, saveCore, fetchAll]);

  // Handle contradiction: update existing memory with new fact
  const resolveContradiction = useCallback(async (existingId, newFact) => {
    const core = coreMemories.find(m => m.id === existingId);
    const working = workingMemories.find(m => m.id === existingId);
    if (core || working) {
      await updateMemory(existingId, !!core, newFact);
    }
  }, [coreMemories, workingMemories, updateMemory]);

  // Purge expired working memories and trim to MAX_WORKING
  const runCleanup = useCallback(async () => {
    try {
      const token = await getToken();
      const now = new Date();
      const toDelete = [];

      // Remove expired
      for (const m of workingMemories) {
        if (m.expirationDate && new Date(m.expirationDate) < now) {
          toDelete.push(m.id);
        }
      }

      // If still over limit after expiry removal, remove lowest relevance
      const remaining = workingMemories.filter(m => !toDelete.includes(m.id));
      if (remaining.length > MAX_WORKING) {
        const sorted = [...remaining].sort((a, b) => a.relevanceScore - b.relevanceScore);
        const excess = sorted.slice(0, remaining.length - MAX_WORKING);
        excess.forEach(m => toDelete.push(m.id));
      }

      for (const id of toDelete) {
        await deleteItem(token, WORKING_LIST, id);
      }

      // Flag core memories unused for 24 months
      const twoYearsAgo = new Date();
      twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);
      for (const m of coreMemories) {
        const lastUsed = m.lastUsed ? new Date(m.lastUsed) : new Date(m.createdDate);
        if (lastUsed < twoYearsAgo && !m.flaggedForReview) {
          await updateItem(token, CORE_LIST, m.id, { FlaggedForReview: true });
        }
      }

      if (toDelete.length > 0) await fetchAll();
    } catch (err) {
      console.warn('Memory cleanup error:', err.message);
    }
  }, [getToken, workingMemories, coreMemories, fetchAll]);

  // Auto-promote working memories with high usage to core
  const autoPromote = useCallback(async () => {
    const candidates = workingMemories.filter(m => m.timesUsed >= 10 && m.relevanceScore >= 80);
    for (const m of candidates) {
      await promoteToCore(m.id);
    }
  }, [workingMemories, promoteToCore]);

  // Clear all working memory for this user
  const clearAllWorking = useCallback(async () => {
    try {
      const token = await getToken();
      for (const m of workingMemories) {
        await deleteItem(token, WORKING_LIST, m.id);
      }
      setWorkingMemories([]);
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, workingMemories]);

  return {
    coreMemories,
    workingMemories,
    loading,
    error,
    buildMemoryContext,
    saveWorking,
    saveCore,
    markUsed,
    updateMemory,
    deleteMemory,
    promoteToCore,
    resolveContradiction,
    runCleanup,
    autoPromote,
    clearAllWorking,
    refetch: fetchAll,
  };
}

function mapCore(it) {
  return {
    id: it.id,
    fact: it.Fact || it.Title || '',
    createdDate: it.CreatedDate || '',
    lastUsed: it.LastUsed || '',
    timesUsed: it.TimesUsed || 0,
    promotedFrom: it.PromotedFrom || '',
    flaggedForReview: it.FlaggedForReview || false,
    createdBy: it.CreatedBy || '',
  };
}

function mapWorking(it) {
  return {
    id: it.id,
    fact: it.Fact || it.Title || '',
    createdDate: it.CreatedDate || '',
    lastUsed: it.LastUsed || '',
    timesUsed: it.TimesUsed || 0,
    expirationDate: it.ExpirationDate || '',
    relevanceScore: it.RelevanceScore || 0,
    createdBy: it.CreatedBy || '',
  };
}
